import { message } from 'antd';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { appLog } from '~src/utils/log';
import { formatDate } from '~src/utils/formatDate';
import { DB_STATE } from '~src/types/enums';
import { DbQuestion } from '~src/types/db';

export const useQACopy = () => {
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Copy questions and answers to clipboard as TSV (Excel-compatible format)
   */
  const handleCopyToClipboard = useCallback(
    async (
      questions: DbQuestion[],
      anonymousMode: boolean,
      isVendorSide: boolean
    ) => {
      try {
        setIsLoading(true);

        if (!questions.length) {
          message.info(intl.formatMessage({ id: 'qa-copy-modal-empty' }));
          return;
        }

        // Define headers based on anonymous mode
        const headers =
          anonymousMode || isVendorSide
            ? [
                intl.formatMessage({ id: 'qa-table-question' }),
                intl.formatMessage({ id: 'qa-table-question-date' }),
                intl.formatMessage({ id: 'qa-table-answer' }),
                intl.formatMessage({ id: 'qa-table-answer-date' }),
              ].join('\t')
            : [
                intl.formatMessage({ id: 'qa-table-question' }),
                intl.formatMessage({ id: 'qa-table-author' }),
                intl.formatMessage({ id: 'qa-table-question-date' }),
                intl.formatMessage({ id: 'qa-table-answer' }),
                intl.formatMessage({ id: 'qa-table-answer-author' }),
                intl.formatMessage({ id: 'qa-table-answer-date' }),
              ].join('\t');

        // Generate rows
        const rows = questions
          .flatMap((question) => {
            const questionText = cleanTextForTSV(question.question || '');
            const questionTime = formatDate(question.created_at, 'long');
            const questionAuthor = getAuthorForExport(
              question.created_by,
              anonymousMode || isVendorSide,
              intl
            );

            // Filter valid answers
            const validAnswers = (question.answers || []).filter(
              (a) =>
                a.state !== DB_STATE.REJECTED && a.state !== DB_STATE.REMOVED
            );

            if (!validAnswers.length) {
              // Question with no answers
              if (anonymousMode || isVendorSide) {
                return `${questionText}\t${questionTime}\t\t`;
              } else {
                return `${questionText}\t${questionAuthor}\t${questionTime}\t\t\t`;
              }
            }

            // Question with answers - create a row for each answer
            return validAnswers.map((answer) => {
              const answerText = cleanTextForTSV(answer.answer || '');
              const answerTime = formatDate(answer.created_at, 'long');
              const answerAuthor = getAuthorForExport(
                answer.created_by,
                anonymousMode || isVendorSide,
                intl
              );

              if (anonymousMode || isVendorSide) {
                return `${questionText}\t${questionTime}\t${answerText}\t${answerTime}`;
              } else {
                return `${questionText}\t${questionAuthor}\t${questionTime}\t${answerText}\t${answerAuthor}\t${answerTime}`;
              }
            });
          })
          .join('\n');

        const tsvContent = `${headers}\n${rows}`;

        // Copy to clipboard
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(tsvContent);
        } else {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = tsvContent;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }

        message.success(intl.formatMessage({ id: 'qa-copy-modal-success' }));
      } catch (error) {
        appLog('handleCopyToClipboard error:', error);
        message.error(intl.formatMessage({ id: 'failed' }));
      } finally {
        setIsLoading(false);
      }
    },
    [intl]
  );

  return {
    handleCopyToClipboard,
    isLoading,
  };
};

/**
 * Get author display for export (TSV format)
 */
const getAuthorForExport = (
  author: any,
  anonymous: boolean,
  intl: any
): string => {
  if (anonymous) {
    return intl.formatMessage({ id: 'qa-vendor-anonymous' });
  }

  if (!author) {
    return '';
  }

  // Format: "Full Name | email@example.com | Company Name"
  const parts: string[] = [];

  if (author.full_name) {
    parts.push(author.full_name);
  }

  if (author.email) {
    parts.push(author.email);
  }

  if (author.company_name) {
    parts.push(author.company_name);
  }

  return parts.join(' | ');
};

/**
 * Clean text for TSV export - replace tabs and newlines with spaces
 */
const cleanTextForTSV = (text: string): string => {
  return text
    .replace(/\t/g, ' ') // Replace tabs with spaces
    .replace(/\n/g, ' ') // Replace newlines with spaces
    .replace(/\r/g, '') // Remove carriage returns
    .trim();
};
